const SHA256_CHECKSUM = /^sha256:([0-9a-f]{64})$/i

type Sha256Digest = (content: ArrayBuffer) => Promise<ArrayBuffer>
type ScheduleTask = (task: () => void) => void

export interface CATXliffExportFile {
  blob: Blob
  fileName: string
  checksum?: string
}

interface DownloadCATXliffExportOptions extends CATXliffExportFile {
  digestSha256?: Sha256Digest
  triggerDownload?: (url: string, fileName: string) => void
  createObjectUrl?: (blob: Blob) => string
  revokeObjectUrl?: (url: string) => void
  scheduleRevoke?: ScheduleTask
}

export class CATXliffIntegrityError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "CATXliffIntegrityError"
  }
}

export class CATXliffExportRejectedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "CATXliffExportRejectedError"
  }
}

const NON_ACTIONABLE_EXPORT_ERRORS = new Set([
  "BadRequest",
  "ParsingError",
  "InternalError",
  "XLIFF export failed",
  "XLIFF export timed out",
])

export const getCatXliffExportFailureReason = (
  error: unknown
): string | null => {
  if (!(error instanceof CATXliffExportRejectedError)) return null
  const message = error.message.trim()
  if (
    !message ||
    message.length > 240 ||
    message.includes("\n") ||
    NON_ACTIONABLE_EXPORT_ERRORS.has(message)
  ) {
    return null
  }
  return message
}

const defaultDigestSha256: Sha256Digest = (content) =>
  globalThis.crypto.subtle.digest("SHA-256", content)

const toHex = (content: ArrayBuffer) =>
  Array.from(new Uint8Array(content), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("")

export const readVerifiedCatXliffExport = async ({
  blob,
  checksum,
}: CATXliffExportFile): Promise<string> => {
  const expectedDigest = SHA256_CHECKSUM.exec(checksum ?? "")?.[1]?.toLowerCase()
  if (!expectedDigest) {
    throw new CATXliffIntegrityError("Unsupported XLIFF checksum")
  }
  const content = await blob.arrayBuffer()
  const actualDigest = toHex(await defaultDigestSha256(content))
  if (actualDigest !== expectedDigest) {
    throw new CATXliffIntegrityError("XLIFF checksum mismatch")
  }
  return new TextDecoder().decode(content)
}

const triggerBrowserDownload = (url: string, fileName: string) => {
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

const scheduleNextTask: ScheduleTask = (task) => {
  setTimeout(task, 0)
}

export const downloadCatXliffExport = async ({
  blob,
  fileName,
  checksum,
  digestSha256 = defaultDigestSha256,
  triggerDownload = triggerBrowserDownload,
  createObjectUrl = URL.createObjectURL,
  revokeObjectUrl = URL.revokeObjectURL,
  scheduleRevoke = scheduleNextTask,
}: DownloadCATXliffExportOptions): Promise<void> => {
  if (checksum) {
    const expectedDigest = SHA256_CHECKSUM.exec(checksum)?.[1]?.toLowerCase()
    if (!expectedDigest) {
      throw new CATXliffIntegrityError("Unsupported XLIFF checksum")
    }
    let actualDigest: string
    try {
      actualDigest = toHex(await digestSha256(await blob.arrayBuffer()))
    } catch {
      throw new CATXliffIntegrityError("Unable to verify XLIFF checksum")
    }
    if (actualDigest !== expectedDigest) {
      throw new CATXliffIntegrityError("XLIFF checksum mismatch")
    }
  }

  const objectUrl = createObjectUrl(blob)
  try {
    triggerDownload(objectUrl, fileName)
  } finally {
    scheduleRevoke(() => revokeObjectUrl(objectUrl))
  }
}
